import React, { useState } from 'react';
import { Layout } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import type { Location, NavigateFunction } from 'react-router-dom';
import type { Dayjs } from 'dayjs';
import { Tooltip } from '@hydrodigital/design-system';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import type { UserLocation } from '@/domain/Location/Location';
import type { AppDispatch } from '@/redux/store';
import useScreenSize from '@/hooks/useScreenSize';
import ModuleMenu from '@/basicsComponents/MenuModule';
import NWLLogoCollapsed from '/img/NWL_logo_collapsed.png';
import { FUTURE_FEATURES, showNavItem } from './constants';
import { readSecurityUser } from './hooks/useSecurityUser';
import type { SecurityUser } from './hooks/useSecurityUser';
import { useSelectedMapMode } from './hooks/useSelectedMapMode';
import { useModeValidations } from './hooks/useModeValidations';
import { useOrganizationSettings } from './hooks/useOrganizationSettings';
import HeaderBrand from './components/HeaderBrand';
import HeaderDatePicker from './components/HeaderDatePicker';
import type { DateRangeTuple } from './components/HeaderDatePicker';
import HeaderNavIcons from './components/HeaderNavIcons';
import type { ChatToggleHandler } from './ChatButton';
import IntervalSelector from './components/IntervalSelector';
import MapModeMenu from './components/MapModeMenu';
import UserMenu from './components/UserMenu';
import ContactSupportModal from './components/ContactSupportModal';
import TooltipLabel from './components/TooltipLabel';

const { Header } = Layout;

interface DashboardHeaderProps {
	companyName: string;
	name: string;
	activitiesCount: number;
	security: SecurityUser;
	contextValue: DashboardContextValueInterface;
	setContextValue: React.Dispatch<React.SetStateAction<DashboardContextValueInterface>>;
	showCalendar: boolean;
	setShowCalendar: (show: boolean) => void;
	dates: DateRangeTuple | null;
	setDates: (dates: DateRangeTuple) => void;
	setSelectedTime1: (time: Dayjs) => void;
	setSelectedTime2: (time: Dayjs) => void;
	setSelectionPhase: (phase: 'start' | 'end') => void;
	setShowLogoutModal: (show: boolean) => void;
	intervalRefresh: number;
	handleClickSelect: (value: number | string) => void;
	handleChangeInterval: (value: number) => void;
	openModalKeplerAnalysisHistorial: () => void;
	handleSelectModule: (moduleId: number) => void;
	toggle: (handler: ChatToggleHandler) => void;
	navigate: NavigateFunction;
	dispatch: AppDispatch;
	location: Location;
	locations: UserLocation[] | null;
	setShowInflowWarningModal: (show: boolean) => void;
	setShowDmaWarningModal: (show: boolean) => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
	companyName,
	name,
	activitiesCount,
	security,
	contextValue,
	setContextValue,
	showCalendar,
	setShowCalendar,
	dates,
	setDates,
	setSelectedTime1,
	setSelectedTime2,
	setSelectionPhase,
	setShowLogoutModal,
	intervalRefresh,
	handleClickSelect,
	handleChangeInterval,
	openModalKeplerAnalysisHistorial,
	handleSelectModule,
	toggle,
	navigate,
	dispatch,
	location,
	locations,
	setShowInflowWarningModal,
	setShowDmaWarningModal,
}) => {
	const { width } = useScreenSize();
	const isXLScreen = width > 1441;
	const isSmallScreen = width <= 1024;

	const userData = readSecurityUser();
	const modules = userData?.modules || [];
	const role = userData?.role || security?.role;

	const { selectedMode, setSelectedMode, selectedModeLabel: rawModeLabel } = useSelectedMapMode();

	const { validateInflowMode, isWaterNetwork, isDrainageNetwork, analysisModeLabel, selectedModeLabel } =
		useModeValidations({
			contextValue,
			setContextValue,
			locations,
			selectedMode,
			rawModeLabel,
			setShowInflowWarningModal,
			setShowDmaWarningModal,
		});

	const { clientSuccessManagerName, clientSuccessManagerEmail } = useOrganizationSettings(
		security?.organizationId,
	);

	const [openMenu, setOpenMenu] = useState<'map' | 'user' | null>(null);
	const [showSupportModal, setShowSupportModal] = useState(false);

	return (
		<Header
			style={{
				padding: 0,
				background: 'transparent',
				height: '76px',
				zIndex: '9',
				position: 'absolute',
				top: '0px',
				width: '100%',
			}}
			className="flex justify-between"
		>
			<HeaderBrand
				companyName={companyName}
				isXLScreen={isXLScreen}
				isSmallScreen={isSmallScreen}
				width={width}
				pathname={location.pathname}
				selectedLocation={contextValue.selectedLocation}
				locations={locations}
				navigate={navigate}
				dispatch={dispatch}
				setContextValue={setContextValue}
				handleClickSelect={handleClickSelect}
			/>

			<HeaderDatePicker
				dates={dates}
				width={width}
				isSmallScreen={isSmallScreen}
				showCalendar={showCalendar}
				timeZone={Number(userData?.timeZone ?? 0)}
				pathname={location.pathname}
				setShowCalendar={setShowCalendar}
				setDates={setDates}
				setSelectedTime1={setSelectedTime1}
				setSelectedTime2={setSelectedTime2}
				setSelectionPhase={setSelectionPhase}
				setContextValue={setContextValue}
			/>

			<div className="my-2.5 ml-2.5 self-center bg-white rounded-lg mr-[5px] px-2.5 py-3.5">
				<div className="flex items-center leading-normal">
					<IntervalSelector
						intervalRefresh={intervalRefresh}
						isSmallScreen={isSmallScreen}
						onChangeInterval={handleChangeInterval}
					/>

					<MapModeMenu
						selectedMode={selectedMode}
						setSelectedMode={setSelectedMode}
						selectedModeLabel={selectedModeLabel}
						isWaterNetwork={isWaterNetwork}
						isDrainageNetwork={isDrainageNetwork}
						analysisModeLabel={analysisModeLabel}
						isSmallScreen={isSmallScreen}
						modules={modules}
						contextValue={contextValue}
						setContextValue={setContextValue}
						navigate={navigate}
						dispatch={dispatch}
						open={openMenu === 'map'}
						onOpenChange={(open) => setOpenMenu(open ? 'map' : null)}
						validateInflowMode={validateInflowMode}
						onInvalidInflow={() => setShowInflowWarningModal(true)}
						openModalKeplerAnalysisHistorial={openModalKeplerAnalysisHistorial}
					/>

					{companyName === 'Northumbrian Water' && (
						<Tooltip position="down" text="NWL Dashboard">
							<div className="flex items-center h-8">
								<img
									src={NWLLogoCollapsed}
									alt="nwl_logo"
									style={{ height: '100%', marginRight: '20px', cursor: 'pointer' }}
									data-testid="nwl-logo"
									onClick={() => {
										navigate('/nwl');
									}}
								/>
							</div>
						</Tooltip>
					)}

					{!showNavItem(import.meta.env.VITE_HEADER_SHOW_MODULE_MENU, location.pathname) && (
						<div
							style={{
								height: '24px',
								borderRight: '1px solid rgba(5, 5, 5, 0.06)',
							}}
							className="ml-2 mr-2 self-center"
						/>
					)}
					{FUTURE_FEATURES.moduleMenu &&
						showNavItem(import.meta.env.VITE_HEADER_SHOW_MODULE_MENU, location.pathname) && (
							<div className="ml-2 mr-2">
								<ModuleMenu handleSelectModule={handleSelectModule} />
							</div>
						)}

					<HeaderNavIcons
						modules={modules}
						activitiesCount={activitiesCount}
						contextValue={contextValue}
						setContextValue={setContextValue}
						navigate={navigate}
						pathname={location.pathname}
						toggle={toggle}
					/>

					<UserMenu
						name={name}
						role={role}
						isSmallScreen={isSmallScreen}
						open={openMenu === 'user'}
						onOpenChange={(open) => setOpenMenu(open ? 'user' : null)}
						navigate={navigate}
						onContactSupport={() => {
							setOpenMenu(null);
							setShowSupportModal(true);
						}}
						onLogout={() => setShowLogoutModal(true)}
					/>

					{security.role !== 'Operator' && (
						<Tooltip position="down" minWidth={20} slot={<TooltipLabel>Settings</TooltipLabel>}>
							<div className="flex items-center h-8">
								<SettingOutlined
									style={{
										fontSize: '20px',
										cursor: 'pointer',
									}}
									onClick={() => {
										navigate('/locations');
									}}
									rev={undefined}
								/>
							</div>
						</Tooltip>
					)}
				</div>
			</div>

			<ContactSupportModal
				isOpen={showSupportModal}
				onClose={() => setShowSupportModal(false)}
				userName={name}
				managerName={clientSuccessManagerName}
				managerEmail={clientSuccessManagerEmail}
			/>
		</Header>
	);
};

export default DashboardHeader;
